import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { createLead, getCategories } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Sparkles, MapPin, Wrench, FileText, CalendarDays, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function LeadRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [structured, setStructured] = useState({});
  const [specializationsByFag, setSpecializationsByFag] = useState({});
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    category: '',
    specialization: '',
    postal_code: '',
    description: '',
    preferred_date: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/find-automatisk');
      return;
    }
    if (user.user_type === 'company') {
      toast.error('Kun private brugere kan bruge automatisk matching');
      navigate('/company-dashboard');
      return;
    }
    getCategories()
      .then((res) => {
        setStructured(res.data.structured || {});
        setSpecializationsByFag(res.data.specializations || {});
      })
      .catch(() => {});
  }, [user, navigate]);

  const canNext = () => {
    if (step === 1) return !!form.category;
    if (step === 2) return /^\d{4}$/.test(form.postal_code.trim());
    if (step === 3) return form.description.trim().length >= 10;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await createLead({
        category: form.category,
        specialization: form.specialization || null,
        postal_code: form.postal_code.trim(),
        description: form.description.trim(),
        preferred_date: form.preferred_date || null,
      });
      const matchCount = (res.data.assignments || []).length;
      if (matchCount === 0) {
        toast.warning('Ingen håndværkere fundet inden for 50 km. Prøv et andet postnummer.');
      } else {
        toast.success(`Din anmodning er sendt til ${matchCount} håndværker${matchCount > 1 ? 'e' : ''}`);
      }
      navigate('/dashboard?tab=leads');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke oprette anmodning');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-10" data-testid="lead-request-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bronze-shine text-white text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Automatisk matching
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Find en håndværker automatisk</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Beskriv dit projekt, så matcher vi dig med op til 3 håndværkere i dit område. Du vælger selv den, du vil arbejde med.
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8 max-w-md mx-auto">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex-1 flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  step >= s ? 'bronze-shine text-white' : 'bg-stone-200 text-stone-500'
                }`}
                data-testid={`lead-step-${s}`}
              >
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 4 && <div className={`flex-1 h-0.5 mx-2 ${step > s ? 'bg-[#c97f59]' : 'bg-stone-200'}`} />}
            </div>
          ))}
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            {step === 1 && (
              <>
                <CardTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" />Hvilken type håndværker?</CardTitle>
                <CardDescription>Vælg den kategori der bedst passer til dit projekt</CardDescription>
              </>
            )}
            {step === 2 && (
              <>
                <CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" />Hvor skal arbejdet udføres?</CardTitle>
                <CardDescription>Vi finder håndværkere inden for 50 km af dit postnummer</CardDescription>
              </>
            )}
            {step === 3 && (
              <>
                <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Beskriv opgaven</CardTitle>
                <CardDescription>Jo mere information du giver, jo bedre bud får du</CardDescription>
              </>
            )}
            {step === 4 && (
              <>
                <CardTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5" />Hvornår vil du gerne starte?</CardTitle>
                <CardDescription>Valgfrit – hjælper håndværkeren med at planlægge</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <div className="space-y-4">
                {Object.entries(structured).map(([group, cats]) => (
                  <div key={group}>
                    {Object.keys(structured).length > 1 && (
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{group}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {cats.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setForm({ ...form, category: c, specialization: '' })}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                            form.category === c
                              ? 'bronze-shine border-transparent text-white shadow-md'
                              : 'bg-white border-stone-200 hover:border-[#c97f59] hover:text-[#c97f59]'
                          }`}
                          data-testid={`lead-category-${c}`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Speciale (valgfrit) — dukker op når kategori er valgt og har specialer */}
                {form.category && (specializationsByFag[form.category] || []).length > 0 && (
                  <div className="pt-4 border-t" data-testid="lead-specialization-picker">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      Speciale (valgfrit)
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, specialization: '' })}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                          !form.specialization
                            ? 'bronze-shine border-transparent text-white'
                            : 'bg-white border-stone-200 hover:border-[#c97f59]'
                        }`}
                        data-testid="lead-specialization-all"
                      >
                        Alle specialer
                      </button>
                      {(specializationsByFag[form.category] || []).map((spec) => {
                        const active = form.specialization === spec;
                        return (
                          <button
                            key={spec}
                            type="button"
                            onClick={() => setForm({ ...form, specialization: spec })}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                              active
                                ? 'bronze-shine border-transparent text-white'
                                : 'bg-white border-stone-200 hover:border-[#c97f59] hover:text-[#c97f59]'
                            }`}
                            data-testid={`lead-specialization-${spec}`}
                          >
                            {spec}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Vælg et speciale, hvis du kun vil matches med håndværkere, der arbejder præcis med dette.
                    </p>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-2">
                <Label htmlFor="postal">Postnummer</Label>
                <Input
                  id="postal"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{4}"
                  maxLength={4}
                  placeholder="fx 2100"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value.replace(/\D/g, '') })}
                  className="text-lg h-14 max-w-[180px]"
                  data-testid="lead-postal-input"
                />
                <p className="text-xs text-muted-foreground">4-cifret dansk postnummer</p>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-2">
                <Label htmlFor="description">Beskrivelse</Label>
                <Textarea
                  id="description"
                  placeholder="Beskriv opgaven så detaljeret som muligt – hvad skal laves, størrelse, materialer osv."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={6}
                  data-testid="lead-description-input"
                />
                <p className="text-xs text-muted-foreground">Minimum 10 tegn ({form.description.trim().length}/10)</p>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-2">
                <Label htmlFor="date">Ønsket startdato</Label>
                <Input
                  id="date"
                  type="date"
                  value={form.preferred_date}
                  onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                  data-testid="lead-date-input"
                />
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 border-t">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
                data-testid="lead-prev-button"
              >
                Tilbage
              </Button>
              {step < 4 ? (
                <Button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  disabled={!canNext()}
                  className="bronze-shine border-0 text-white"
                  data-testid="lead-next-button"
                >
                  Næste
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  className="bronze-shine border-0 text-white"
                  data-testid="lead-submit-button"
                >
                  {submitting ? 'Sender...' : 'Send anmodning'}
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary preview */}
        {step > 1 && form.category && (
          <div className="mt-6 p-4 bg-white rounded-xl border text-sm">
            <p className="font-semibold mb-2">Din anmodning</p>
            <div className="space-y-1 text-muted-foreground">
              <p><Wrench className="w-3 h-3 inline mr-1" /> {form.category}{form.specialization ? ` — ${form.specialization}` : ''}</p>
              {form.postal_code && <p><MapPin className="w-3 h-3 inline mr-1" /> Postnummer {form.postal_code}</p>}
              {form.description && <p className="line-clamp-2"><FileText className="w-3 h-3 inline mr-1" /> {form.description}</p>}
              {form.preferred_date && <p><CalendarDays className="w-3 h-3 inline mr-1" /> {form.preferred_date}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
